import React, { useState } from 'react';
import { Utensils, TreePine, Building2, ShoppingBag, MapPin, Star, ExternalLink, ChevronRight, Wine, Castle } from 'lucide-react';

const LocalGuideSection = () => {
  const [activeCategory, setActiveCategory] = useState('food');
  const [expandedCards, setExpandedCards] = useState({});

  const toggleExpand = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const categories = [
    { id: 'food', label: 'Food & Drink', icon: Utensils },
    { id: 'nature', label: 'Parks & Nature', icon: TreePine },
    { id: 'sights', label: 'Sightseeing', icon: Castle },
    { id: 'shopping', label: 'Shopping', icon: ShoppingBag },
  ];

  const guidebookData = {
    food: [
      {
        name: "The Jolly Tanners",
        description: "Staplefield's beautifully refurbished country pub with cosy log fires, exposed beams, and a large beer garden. Food-led with locally sourced produce, serving three local ales. Perfect after a countryside walk.",
        locals: 45,
        type: "Country Pub",
        image: "https://customer-assets.emergentagent.com/job_11ad18c7-31a2-46e1-a673-655fe5369514/artifacts/90p2qyd8_2025-07-18.jpg"
      },
      {
        name: "Tamasha",
        description: "Handcross's top-rated Indian restaurant serving authentic curries, tandoori grills, and biryanis. Fresh, flavourful food with generous portions. Dine-in or takeaway available. A true local gem.",
        locals: 32,
        type: "Indian Restaurant",
        image: "https://customer-assets.emergentagent.com/job_11ad18c7-31a2-46e1-a673-655fe5369514/artifacts/33ptlje0_2025-09-08.jpg"
      },
      {
        name: "Jo.Co",
        description: "Portuguese-inspired café in Handcross and Balcombe. Famous for excellent coffee, Pastéis de Nata (custard tarts), hearty breakfasts, and homemade cakes. Friendly, welcoming vibe loved by locals.",
        locals: 28,
        type: "Café",
        image: "https://customer-assets.emergentagent.com/job_11ad18c7-31a2-46e1-a673-655fe5369514/artifacts/5gzqdlh6_unnamed.jpg"
      },
      {
        name: "Ockenden Manor",
        description: "Hotel with formal dining room and super Spa.",
        locals: 20,
        type: "Fine Dining & Spa",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/j9rgwxht_google_place_photos%20copy%203.jpeg"
      },
      {
        name: "The Half Moon",
        description: "Great pub. Always busy so need to book. Super garden.",
        locals: 10,
        type: "Pub",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/5ckpek9g_google_place_photos%20copy%204.jpeg"
      },
      {
        name: "Rose & Crown Cuckfield",
        description: "Busy pub serving high end food in lovely village of Cuckfield.",
        type: "Gastropub",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/lfxi0odu_google_place_photos%20copy.jpeg"
      }
    ],
    nature: [
      {
        name: "National Trust - Nymans",
        description: "Stunning gardens, historic house and ruins. Wonderful walks. Cafe and shop.",
        locals: 137,
        type: "Gardens",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/3buiig3q_google_place_photos%20copy%2010.jpeg"
      },
      {
        name: "High Beeches Gardens",
        description: "With 27 acres of woodland, water gardens, and a collection of rare, exotic plants. A botanical treasure trove and one of Sussex's finest gardens.",
        locals: 12,
        type: "Gardens",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/2kz5waxe_google_place_photos%20copy%207.jpeg"
      },
      {
        name: "Borde Hill Garden",
        description: "Botanical heritage and stunning landscapes. Created by seeds gathered by the Great Plant Hunters. Super adventure playground and children's activities.",
        locals: 52,
        type: "Gardens",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/ik14z76u_google_place_photos%20copy%208.jpeg"
      },
      {
        name: "Wakehurst",
        description: "Home to the Millennium Seed Bank. Fabulous gardens and walking trails and historic house.",
        locals: 125,
        type: "Gardens & Nature",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/fip5uzmw_google_place_photos%20copy%209.jpeg"
      },
      {
        name: "Ashdown Forest",
        description: "Home to AA Milne, Christopher Robin and Winnie the Pooh. Staggering views. Wonderful walks. Play Pooh Sticks on Pooh Sticks Bridge.",
        locals: 176,
        type: "Forest",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/siq0sw4e_google_place_photos%20copy%2011.jpeg"
      },
      {
        name: "Sheffield Park Garden",
        description: "Acres of landscape garden bordered by historic parkland and woodland. Spectacular autumn colours reflected in the lakes.",
        locals: 128,
        type: "Gardens",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/hopkyqed_google_place_photos%20copy%2012.jpeg"
      }
    ],
    sights: [
      {
        name: "Ouse Valley Viaduct",
        description: "A must-visit for influencers and photographers! This stunning 37-arch Victorian masterpiece is accessible via Balcombe Station. Famous for its magnificent architecture and iconic arched photography spots. One of the most Instagrammable locations in Sussex.",
        locals: 89,
        type: "Iconic Landmark",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/6gly5gzb_google_place_photos%20copy%2013.jpeg"
      },
      {
        name: "Arundel Castle",
        description: "A restored and remodelled medieval castle established on Christmas Day 1068. One of England's finest castles.",
        locals: 526,
        type: "Castle",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/o3d8xcwc_google_place_photos%20copy%2014.jpeg"
      },
      {
        name: "Petworth House (National Trust)",
        description: "A stately mansion nestled in the South Downs housing the finest art collection in the care of the National Trust.",
        locals: 595,
        type: "Historic House",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/gryu7cie_unnamed%20copy.jpg"
      },
      {
        name: "Sheffield Park Garden",
        description: "Acres of landscape garden bordered by historic parkland and woodland. Spectacular autumn colours reflected in the lakes.",
        locals: 128,
        type: "Gardens",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/0nrwxv05_google_place_photos%20copy%2015.jpeg"
      },
      {
        name: "Parham House & Gardens",
        description: "One of the country's finest Elizabethan Houses, complete with a Great Hall and Long Gallery. Timeless beauty.",
        locals: 68,
        type: "Historic House",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/zmkww7iq_google_place_photos%20copy%2016.jpeg"
      },
      {
        name: "Standen (National Trust)",
        description: "Arts and Crafts family home with Morris & Co. interiors, set in a beautiful hillside garden with stunning views across the Sussex countryside.",
        locals: 11,
        type: "Historic House",
        image: "https://customer-assets.emergentagent.com/job_04ca7524-04cf-4327-9f0b-2136a5264aeb/artifacts/i04j5gl5_unnamed%20copy%202.jpg"
      }
    ],
    shopping: [
      {
        name: "Cuckfield",
        description: "Boutique heaven with gorgeous clothes, antique and lifestyle shops along with pubs, tea shops and a fabulous spa at Ockenden Manor.",
        locals: 11,
        type: "Village",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/xq8mrkhs_google_place_photos%20copy%2017.jpeg"
      },
      {
        name: "Lindfield",
        description: "Beautiful village with a super selection of shops and restaurants.",
        locals: 18,
        type: "Village",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/njoy1d9e_google_place_photos-2%20copy%202.jpeg"
      },
      {
        name: "Petworth",
        description: "Pretty town with delightful mix of antique shops and restaurants.",
        locals: 111,
        type: "Town",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/1ihzvlz1_google_place_photos%20copy%2018.jpeg"
      },
      {
        name: "Lewes",
        description: "Pretty town with delightful mix of shops including many antique shops and restaurants. Famous for its castle and bonfire night celebrations.",
        locals: 290,
        type: "Town",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/hjoj99hz_google_place_photos%20copy%2019.jpeg"
      },
      {
        name: "London",
        description: "All its delights is a mere 40 minute train ride from our village station.",
        locals: 881,
        type: "City",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/fpehmhue_google_place_photos%20copy%2020.jpeg"
      },
      {
        name: "Brighton",
        description: "Brighton has it all! Just 25 minutes by train.",
        locals: 585,
        type: "City",
        image: "https://customer-assets.emergentagent.com/job_6c7b00b0-b693-4317-b49f-43ae68e89d1d/artifacts/dn8jn9dn_google_place_photos%20copy%2021.jpeg"
      }
    ]
  };

  const activeData = guidebookData[activeCategory] || [];

  return (
    <section className="py-10 md:py-16 px-4 md:px-6 bg-[#FAF7F2]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 md:mb-10">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2" style={{fontFamily: 'Cormorant Garamond, Georgia, serif'}}>
            Susanna's Local Guide
          </h2>
          <p className="text-muted-foreground text-sm md:text-base max-w-2xl mx-auto">
            Hand-picked recommendations from your host for the best of West Sussex
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-6 md:mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(cat.id);
                setExpandedCards({});
              }}
              className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all ${
                activeCategory === cat.id
                  ? 'bg-[#FF5A5F] text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <cat.icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Recommendations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {activeData.slice(0, 6).map((place, index) => (
            <div
              key={index}
              className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow group"
              data-testid={`local-guide-card-${index}`}
            >
              {/* Image */}
              <div className="relative h-36 md:h-40 overflow-hidden">
                <img
                  src={place.image}
                  alt={place.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                {/* Type badge on image */}
                <span className="absolute bottom-2 left-3 text-[10px] md:text-xs text-white font-medium uppercase tracking-wide bg-black/30 backdrop-blur-sm px-2 py-0.5 rounded-full">
                  {place.type}
                </span>
                {/* Locals badge */}
                {place.locals && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 shadow-sm">
                    <Star className="w-2.5 h-2.5 md:w-3 md:h-3 fill-[#FFB400] text-[#FFB400]" />
                    <span className="text-[10px] md:text-xs text-gray-700 font-medium">{place.locals}</span>
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="p-4 md:p-5">
                <h3 className="font-semibold text-foreground text-sm md:text-base mb-1.5" style={{fontFamily: 'Cormorant Garamond, Georgia, serif'}}>
                  {place.name}
                </h3>
                <p className={`text-gray-600 text-xs md:text-sm leading-relaxed ${expandedCards[index] ? '' : 'line-clamp-2'}`}>
                  {place.description}
                </p>
                {place.description.length > 80 && (
                  <button
                    onClick={() => toggleExpand(index)}
                    className="text-[#FF5A5F] hover:text-[#E04E53] text-sm md:text-base font-medium mt-2 py-2 px-3 -ml-3 rounded-lg hover:bg-red-50 transition-all"
                  >
                    {expandedCards[index] ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* View Full Guidebook Link */}
        <div className="text-center mt-6 md:mt-8">
          <a
            href="https://www.airbnb.co.uk/s/guidebooks?refinement_paths[]=/guidebooks/529380"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#FF5A5F] hover:text-[#E04E53] font-medium transition-colors text-sm md:text-base"
            data-testid="guidebook-link"
            onClick={() => { if (window.va) window.va('event', { name: 'airbnb_click', data: { location: 'local_guide_guidebook' } }); }}
          >
            <span>View full guidebook on Airbnb</span>
            <ExternalLink className="w-3.5 h-3.5 md:w-4 md:h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default LocalGuideSection;
